import {
  BRIDGE_SEATS,
  type BridgeSeat,
  WHIST_TARGET,
  cardId,
  matchStatus,
  sortHand,
  whistViewFor,
} from '@chessmate/game-engine';
import { PlayingCard, SUIT_SYMBOL, CARD_SLOT_CLASS, HAND_CLASS } from '../components/PlayingCard.js';
import { CardTable } from '../components/table/CardTable.js';
import { RoundSummaryPanel, type RoundSummary } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { TrickArea } from '../components/table/TrickArea.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, MY_SIDE, useWhistStore } from '../state/whist-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

const THEIR_SIDE = 'EW';

/**
 * The Whist screen: you in South with a robot partner in North, robot opponents East and West. It
 * renders only from `whistViewFor(game, HUMAN_SEAT)`, so a hand you may not see is never in the data this
 * component can reach. Rules: docs/proposals/whist-and-pirate-bridge.md.
 */
export function WhistScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useWhistStore((s) => s.game);
  const result = useWhistStore((s) => s.result);
  const score = useWhistStore((s) => s.score);
  const length = useWhistStore((s) => s.length);
  const gameWinner = useWhistStore((s) => s.gameWinner);
  const dealNumber = useWhistStore((s) => s.dealNumber);
  const seed = useWhistStore((s) => s.seed);
  const strength = useWhistStore((s) => s.strength);
  const fourColour = useWhistStore((s) => s.fourColour);
  const tournament = useWhistStore((s) => s.tournament);
  const play = useWhistStore((s) => s.play);
  const nextDeal = useWhistStore((s) => s.nextDeal);
  const newGame = useWhistStore((s) => s.newGame);
  const setStrength = useWhistStore((s) => s.setStrength);
  const setFourColour = useWhistStore((s) => s.setFourColour);
  const setLength = useWhistStore((s) => s.setLength);
  const startTournament = useWhistStore((s) => s.startTournament);
  const endTournament = useWhistStore((s) => s.endTournament);

  const identify = useSeatIdentity(BRIDGE_SEATS, (x) => t(`bridge-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = whistViewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`bridge-seat-${s}`);
  const suitName = (s: typeof view.trump) => `${SUIT_SYMBOL[s]} ${t(`bridge-suit-${s}`)}`;
  const legal = new Set(view.legalPlays.map(cardId));
  const target = WHIST_TARGET[length];

  let status: string;
  if (view.phase === 'done') {
    status = result
      ? result.winningSide === MY_SIDE
        ? t('whist-deal-won', { n: result.tricks[result.winningSide], points: result.points })
        : t('whist-deal-lost', { n: result.tricks[result.winningSide], points: result.points })
      : '';
  } else if (view.canAct) status = t('whist-turn-you');
  else status = t('whist-turn-wait', { seat: seatName(view.turn) });

  const lastTrick = view.tricks[view.tricks.length - 1];
  const shown = view.trick.length > 0 ? view.trick : (lastTrick?.plays ?? []);
  const winner = view.trick.length === 0 ? lastTrick?.winner : undefined;

  const plate = (seat: BridgeSeat) => {
    const id = identify(seat);
    return (
      <SeatPlate
        bubble={bubbles[seat]?.line}
        testid={id.you ? 'whist-seat-S' : `whist-hand-${seat}`}
        name={id.name}
        tag={id.tag}
        portrait={id.portrait}
        fallbackIcon={id.fallbackIcon}
        score={view.tricksWon[seat === 'N' || seat === 'S' ? MY_SIDE : THEIR_SIDE]}
        scoreLabel={t('whist-tricks', { us: view.tricksWon[MY_SIDE], them: view.tricksWon[THEIR_SIDE] })}
        cardsLeft={id.you ? undefined : view.handSizes[seat]}
        cardsLeftLabel={t('bridge-cards-left', { n: view.handSizes[seat] })}
        active={view.turn === seat && !result}
        you={id.you}
      />
    );
  };

  const summary: RoundSummary | null = result
    ? {
        round: dealNumber,
        outcome: gameWinner ? (gameWinner === MY_SIDE ? 'win' : 'lose') : view.tricksWon[MY_SIDE] > view.tricksWon[THEIR_SIDE] ? 'win' : view.tricksWon[MY_SIDE] < view.tricksWon[THEIR_SIDE] ? 'lose' : 'draw',
        headline: gameWinner ? (gameWinner === MY_SIDE ? t('whist-game-won') : t('whist-game-lost')) : status,
        rows: [
          { key: 'us', label: `${identify('S').name} + ${identify('N').name}`, portrait: identify('N').portrait, delta: `+${view.tricksWon[MY_SIDE]}`, total: score[MY_SIDE], you: true, leading: score[MY_SIDE] >= score[THEIR_SIDE] },
          { key: 'them', label: `${identify('W').name} + ${identify('E').name}`, portrait: identify('W').portrait, delta: `+${view.tricksWon[THEIR_SIDE]}`, total: score[THEIR_SIDE], leading: score[THEIR_SIDE] > score[MY_SIDE] },
        ],
        gameOver: Boolean(gameWinner),
      }
    : null;

  const slot = (seat: BridgeSeat) => {
    const p = shown.find((x) => x.seat === seat);
    return p ? <PlayingCard compact card={p.card} fourColour={fourColour} highlight={winner === seat} /> : <span className={CARD_SLOT_CLASS} aria-hidden="true" />;
  };

  const sessionStatus = tournament ? matchStatus(tournament.session) : null;
  const seriesStatus = tournament ? matchStatus(tournament.series) : null;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="whist-screen" data-appdom-scope="whist">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('whist-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="whist-seed">{t('whist-deal', { n: dealNumber, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="whist-new-game" data-appdom-id="whist-new-game" data-appdom-type="Button">{t('whist-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="whist-exit" data-appdom-id="whist-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="whist-status" data-appdom-id="whist-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        <span className="flex items-center gap-2 font-normal text-fg-muted" data-testid="whist-trump">
          {t('whist-trump', { suit: suitName(view.trump) })}
          <PlayingCard compact card={view.trumpCard} fourColour={fourColour} />
        </span>
        <span className="font-normal text-fg-muted" data-testid="whist-tricks">
          {t('whist-tricks', { us: view.tricksWon[MY_SIDE], them: view.tricksWon[THEIR_SIDE] })}
        </span>
        <span className="font-normal text-fg-muted" data-testid="whist-score">
          {t('whist-score', { us: score[MY_SIDE], them: score[THEIR_SIDE], target })}
        </span>
      </output>

      <ScoreStrip
        testid="whist-score-strip"
        title={t('table-score')}
        note={t('whist-tricks', { us: view.tricksWon[MY_SIDE], them: view.tricksWon[THEIR_SIDE] })}
        rows={[
          { key: 'us', label: `${identify('S').name} + ${identify('N').name}`, value: score[MY_SIDE], delta: view.tricksWon[MY_SIDE] ? `+${view.tricksWon[MY_SIDE]}` : undefined, you: true, leading: score[MY_SIDE] >= score[THEIR_SIDE] },
          { key: 'them', label: `${identify('W').name} + ${identify('E').name}`, value: score[THEIR_SIDE], delta: view.tricksWon[THEIR_SIDE] ? `+${view.tricksWon[THEIR_SIDE]}` : undefined, leading: score[THEIR_SIDE] > score[MY_SIDE] },
        ]}
      />

      <CardTable
        top={plate('N')}
        left={plate('W')}
        right={plate('E')}
        center={<TrickArea testid="whist-trick" label={t('bridge-trick-area')} north={slot('N')} west={slot('W')} east={slot('E')} south={slot('S')} />}
        bottom={
          <section aria-label={t('bridge-hand-of', { seat: seatName(HUMAN_SEAT) })} data-testid="whist-hand-S" className="flex flex-col items-center gap-3">
            {plate(HUMAN_SEAT)}
            <div className={HAND_CLASS}>
              {sortHand(view.hand).map((c) => (
                <PlayingCard key={cardId(c)} card={c} fourColour={fourColour} onPlay={legal.has(cardId(c)) ? () => play(c) : undefined} dimmed={view.canAct && !legal.has(cardId(c))} />
              ))}
            </div>
          </section>
        }
      />

      {summary && (
        <RoundSummaryPanel
          testid="whist-result"
          summary={summary}
          nextLabel={gameWinner ? t('whist-new-game') : t('whist-next-deal')}
          onNext={gameWinner ? newGame : nextDeal}
          nextTestid={gameWinner ? 'whist-play-again' : 'whist-next-deal'}
        />
      )}

      {tournament && sessionStatus && seriesStatus && (
        <section className="glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm text-ink" data-testid="whist-tournament" data-appdom-id="whist-tournament" data-appdom-type="Panel">
          <h2 className="font-extrabold">{t('whist-tournament')}</h2>
          <span data-testid="whist-session">
            {t('whist-session', {
              us: tournament.session.wins[MY_SIDE],
              them: tournament.session.wins[THEIR_SIDE],
              target: tournament.session.options.winsNeeded,
              max: tournament.session.options.maxGames,
            })}
          </span>
          <span data-testid="whist-series">
            {t('whist-series', {
              us: tournament.series.wins[MY_SIDE],
              them: tournament.series.wins[THEIR_SIDE],
              target: tournament.series.options.winsNeeded,
            })}
          </span>
          {seriesStatus.state === 'won' && (
            <strong data-testid="whist-series-result">{seriesStatus.winner === MY_SIDE ? t('whist-series-won') : t('whist-series-lost')}</strong>
          )}
          {seriesStatus.state === 'drawn' && <strong data-testid="whist-series-result">{t('whist-series-drawn')}</strong>}
        </section>
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="whist-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`whist-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <span>{t('whist-length')}</span>
            <div className="flex items-center gap-2">
              {(['short', 'long'] as const).map((l) => (
                <button key={l} type="button" aria-pressed={length === l} data-testid={`whist-length-${l}`} onClick={() => setLength(l)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${length === l ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                  {t(`whist-${l}`, { n: WHIST_TARGET[l] })}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="whist-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <button
            type="button"
            className="min-h-[48px] self-start rounded-lg border border-border px-3 py-1"
            onClick={tournament ? endTournament : startTournament}
            data-testid="whist-tournament-toggle"
          >
            {tournament ? t('whist-tournament-end') : t('whist-tournament-start')}
          </button>
          <p className="font-normal text-fg-muted">{t('whist-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}
